import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useQueryClient } from "react-query";
import { ChevronRight } from "react-ikonate";
import { motion } from "framer-motion";
import styles from "./CartPage.module.css";
import { useHistory } from "react-router-dom";
import ProductImage from "Shared/ProductImage";

import Navbar from "components/Navbar";
import BottomNav from "components/BottomNav";
import CartHeader from "components/CartHeader";
import CartItemList from "components/CartItemList";
import CartTotalDetails from "components/CartTotalDetails";
import ShippingAddressModal from "components/ShippingAddressModal";
import SavedCardsModal from "components/SavedCardsModal";
import CardPaymentModel from "components/CardPaymentModel";
import Modal from "components/Modal";
import Button from "Shared/Button";
import NotificationOverlay from "Shared/NotificationOverlay";

import orderStyles from "../OrderDetails/OrderDetails.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCardBrandIcon } from "../../utils/cardBrandIcon";

import { useTranslation } from "react-i18next";
import {
  useCart,
  getCartItemCount,
  useCheckout,
  usePurchase,
  useRemoveFromCart,
  useAddToCart,
  useUpdateCartItem,
  newIdempotencyKey,
} from "../../hooks/useCart";
import { useAddresses } from "../../hooks/useAddresses";
import { useApiErrorMessage } from "../../hooks/useApiErrorMessage";
import {
  usePaymentMethods,
  useAddPaymentMethod,
} from "../../hooks/usePaymentMethods";
import {
  useLists,
  useListItems,
  useRemoveListItem,
} from "../../hooks/useLists";
import { useContactId } from "../../hooks/useContactId";
import { toNumericId } from "../../utils/toNumericId";
import { resolveImageUrl } from "../../utils/resolveImageUrl";
import { isOutOfStockError } from "../../utils/stockStatus";
import {
  trackCheckoutStarted,
  trackOrderCompleted,
  trackProductRemoved,
  trackPaymentInfoEntered,
} from "../../utils/analytics";

// Business outcomes the API reports on the purchase path that the user can recover
// from by reviewing their order and retrying against a fresh checkout session.
const RECOVERABLE_CHECKOUT_CODES = new Set([
  "CART_CHANGED",
  "SESSION_EXPIRED",
  "SESSION_NOT_PURCHASABLE",
  "CART_ALREADY_CONVERTED",
  // The cart was regenerated because an item is no longer available to this
  // IBO. The replacement cart is already ACTIVE, so a refetch recovers.
  "CART_ABANDONED",
]);

function readAmount(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function CartPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const contactId = useContactId();

  const { data: cartData, isLoading: isCartLoading } = useCart();
  const { data: lists = [] } = useLists();
  const checkout = useCheckout();
  const purchase = usePurchase();
  const removeFromCart = useRemoveFromCart();
  const addToCart = useAddToCart();
  const updateCartItem = useUpdateCartItem();
  const removeListItem = useRemoveListItem();
  const addPaymentMethod = useAddPaymentMethod();
  const { data: addresses = [] } = useAddresses();
  const { data: paymentCards = [] } = usePaymentMethods();
  const cartCount = getCartItemCount(cartData);
  const strandedItemCount = cartData?.strandedItems?.length ?? 0;

  // Find "Saved for Later" list
  const savedForLaterList = useMemo(
    () =>
      lists.find(
        (list) =>
          String(list?.name ?? "")
            .trim()
            .toLowerCase() === "saved for later"
      ),
    [lists]
  );

  const { data: savedForLaterItems = [], isLoading: isSavedForLaterLoading } =
    useListItems(savedForLaterList?.listId);

  const stockInfo = useMemo(() => cartData?.items ?? [], [cartData?.items]);

  // CartGet re-validates stock on every fetch, so any of these rows can flip
  // to out-of-stock between page visits (e.g. someone else bought the last
  // one). Surface it with a blocking popup and keep it out of the checkout
  // flow until the user clears their cart of the affected items.
  const outOfStockCartItems = useMemo(
    () => stockInfo.filter((item) => item?.isOutOfStock),
    [stockInfo]
  );

  // Cart items whose requested quantity exceeds what's actually available -
  // e.g. stock dropped after the item was added. Distinct from isOutOfStock
  // (0 available); this can happen any time quantityAvailable is tracked and
  // lower than the quantity already sitting in the cart.
  const overStockCartItems = useMemo(
    () =>
      stockInfo.filter(
        (item) =>
          !item?.isOutOfStock &&
          typeof item?.quantityAvailable === "number" &&
          (item?.quantity ?? 0) > item.quantityAvailable
      ),
    [stockInfo]
  );
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
  const outOfStockModalShownRef = useRef(false);

  useEffect(() => {
    if (isCartLoading) return;
    if (outOfStockCartItems.length > 0) {
      if (!outOfStockModalShownRef.current) {
        setIsOutOfStockModalOpen(true);
        outOfStockModalShownRef.current = true;
      }
    } else {
      outOfStockModalShownRef.current = false;
      setIsOutOfStockModalOpen(false);
    }
  }, [isCartLoading, outOfStockCartItems]);

  // Normalize saved items to match cart item structure
  const savedItems = useMemo(() => {
    return (Array.isArray(savedForLaterItems) ? savedForLaterItems : []).map(
      (item) => {
        // CONFIGURABLE items come back from /lists/:listId/items with a
        // null top-level memberPrice - pricing only lives on the matching
        // entry in `variants`, keyed by this row's own variationId/variantId.
        const matchedVariant = Array.isArray(item?.variants)
          ? item.variants.find(
              (v) =>
                String(v?.variationId ?? "") ===
                  String(item?.variationId ?? "") &&
                String(v?.variantId ?? "") === String(item?.variantId ?? "")
            )
          : null;

        const resolvedMemberPrice =
          item?.memberPrice ?? matchedVariant?.memberPrice ?? null;

        return {
          ...item,
          id: toNumericId(item?.listProductId ?? item?.id ?? item?.stockId),
          stockId: item?.stockId ?? item?.productId ?? item?.id ?? "",
          title: item?.title ?? "",
          image: resolveImageUrl(item),
          imageUrl: resolveImageUrl(item),
          memberPrice: resolvedMemberPrice ?? item?.unitPrice ?? 0,
          retailPrice: item?.retailPrice ?? null,
          quantity: item?.quantity ?? 1,
          color: item?.color,
          size: item?.size,
          isPreOrder: Boolean(item?.isPreOrder ?? item?.preOrder),
          isOutOfStock: Boolean(item?.isOutOfStock),
        };
      }
    );
  }, [savedForLaterItems]);

  const handleAddSavedItemToCart = async (savedItem) => {
    if (savedItem?.isOutOfStock) return;

    const variationId =
      savedItem?.variationId ??
      savedItem?.variationID ??
      savedItem?.variation?.id ??
      savedItem?.variation?.variationId ??
      null;
    const variantId =
      savedItem?.variantId ??
      savedItem?.variantID ??
      savedItem?.variant?.id ??
      savedItem?.variant?.variantId ??
      null;

    const isThirdParty =
      Boolean(savedItem?.is3rdParty) ||
      (Array.isArray(savedItem?.attributes)
        ? savedItem.attributes.some((attribute) => {
            const type = String(attribute?.type ?? "")
              .trim()
              .toUpperCase();
            const code = String(attribute?.code ?? "")
              .trim()
              .toUpperCase();
            return type === "SOURCE" && code === "SOURCE_3RD_PARTY";
          })
        : false);

    const isConfigurable =
      String(savedItem?.itemType ?? "")
        .trim()
        .toUpperCase() === "CONFIGURABLE";

    // /lists/:listId/items already returns memberPrice/retailPrice from the
    // same source as /stock-items, so there's no need for a separate catalog
    // lookup here.
    const memberCandidate = savedItem?.memberPrice ?? savedItem?.unitPrice ?? 0;
    const retailCandidate = savedItem?.retailPrice ?? memberCandidate;

    const optimisticMemberPrice =
      isThirdParty && retailCandidate > 0 ? retailCandidate : memberCandidate;
    const optimisticRetailPrice = savedItem?.retailPrice ?? null;

    // Generate temporary ID for optimistic update
    const tempCartItemId = `temp-${Date.now()}-${Math.random()}`;

    // Optimistically update cart: add the item. Cache key must match
    // useCart's key ("cart" alone — see useCart.js for why contactId isn't
    // part of it).
    const cartQueryKey = "cart";
    const previousCartData = queryClient.getQueryData(cartQueryKey);
    if (previousCartData) {
      const newCartItem = {
        ...savedItem,
        id: tempCartItemId, // Use temp ID for optimistic update
        memberPrice: optimisticMemberPrice,
        retailPrice: optimisticRetailPrice,
        variationId,
        variantId,
      };
      queryClient.setQueryData(cartQueryKey, {
        ...previousCartData,
        items: [...(previousCartData.items ?? []), newCartItem],
      });
    }

    try {
      // Add to cart with the saved variant choices. Awaiting here (instead of
      // fire-and-forget .mutate) lets us confirm the item was actually
      // accepted before touching the saved list below.
      await addToCart.mutateAsync({
        productId: savedItem?.stockId ?? savedItem?.productId,
        variationId,
        variantId,
        ...(isConfigurable && savedItem?.color
          ? { color: savedItem.color }
          : {}),
        ...(isConfigurable && savedItem?.size ? { size: savedItem.size } : {}),
        quantity: savedItem?.quantity ?? 1,
      });
    } catch (error) {
      // e.g. PRODUCT_NOT_AVAILABLE because the item's visibility group
      // changed. Undo the optimistic cart insert and leave the saved item
      // where it was — it was never actually added.
      if (previousCartData) {
        queryClient.setQueryData(cartQueryKey, previousCartData);
      }
      return;
    }

    // Only remove from the saved list once the add-to-cart call is confirmed.
    if (savedForLaterList?.listId) {
      removeListItem.mutate({
        listId: savedForLaterList.listId,
        listProductId: savedItem?.id ?? savedItem?.listProductId,
        stockId: savedItem?.stockId ?? savedItem?.productId,
      });
    }
  };

  // Checkout should default to the address flagged as the shipping default
  // (useAddresses' isDefault, derived from address.shipping), not the
  // mailing/primary address — falling back to primary, then the first
  // address, only when nothing is flagged as shipping yet.
  const defaultShippingAddress =
    addresses.find((a) => a.isDefault) ??
    addresses.find((a) => a.isPrimary) ??
    addresses[0] ??
    null;
  const [selectedAddress, setSelectedAddress] = useState(null);
  const shippingAddress = selectedAddress ?? defaultShippingAddress;

  const primaryCard =
    paymentCards.find((c) => c.isPrimary) ?? paymentCards[0] ?? null;
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedCardPreview, setSelectedCardPreview] = useState(null);

  const cardIdentityValues = useCallback((card) => {
    const values = [
      card?.membershipCreditCardId,
      card?.raw?.membershipCreditCardId,
      card?.functionCreditCardId,
      card?.raw?.functionCreditCardId,
      card?.cardId,
      card?.id,
    ]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);

    return Array.from(new Set(values));
  }, []);

  const cardsLikelyMatch = useCallback(
    (left, right) => {
      const leftIds = cardIdentityValues(left);
      const rightIds = cardIdentityValues(right);

      const idMatched =
        leftIds.length > 0 &&
        rightIds.some((candidate) => leftIds.includes(candidate));

      const leftLast4 = String(left?.lastFour ?? left?.last4 ?? "").trim();
      const rightLast4 = String(right?.lastFour ?? right?.last4 ?? "").trim();

      const leftNickname = String(
        left?.nickName ?? left?.nickname ?? ""
      ).trim();
      const rightNickname = String(
        right?.nickName ?? right?.nickname ?? ""
      ).trim();

      const last4Matched = leftLast4 && rightLast4 && leftLast4 === rightLast4;
      const nicknameMatched =
        leftNickname && rightNickname && leftNickname === rightNickname;

      return idMatched || (last4Matched && nicknameMatched);
    },
    [cardIdentityValues]
  );

  const activeCard = useMemo(() => {
    if (selectedCardPreview) {
      return selectedCardPreview;
    }

    if (selectedCardId != null) {
      const selectedFromQuery = paymentCards.find((card) => {
        const id = card?.membershipCreditCardId ?? card?.cardId ?? card?.id;
        return String(id) === String(selectedCardId);
      });

      if (selectedFromQuery) return selectedFromQuery;
    }

    return primaryCard;
  }, [paymentCards, selectedCardId, selectedCardPreview, primaryCard]);

  useEffect(() => {
    if (selectedCardId == null) {
      return;
    }

    if (!selectedCardPreview) {
      const fallbackSelected = paymentCards.find((card) => {
        const id = card?.membershipCreditCardId ?? card?.cardId ?? card?.id;
        return String(id) === String(selectedCardId);
      });

      if (fallbackSelected) {
        setSelectedCardPreview(fallbackSelected);
      }
      return;
    }

    const previewId = String(
      selectedCardPreview?.membershipCreditCardId ??
        selectedCardPreview?.cardId ??
        selectedCardPreview?.id ??
        ""
    );
    if (previewId !== String(selectedCardId)) {
      return;
    }

    const refreshedSelectedCard = paymentCards.find((card) =>
      cardsLikelyMatch(card, selectedCardPreview)
    );

    if (refreshedSelectedCard) {
      setSelectedCardPreview(refreshedSelectedCard);
    }
  }, [paymentCards, selectedCardId, selectedCardPreview, cardsLikelyMatch]);

  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [checkoutTotals, setCheckoutTotals] = useState(null);
  const [checkoutNotice, setCheckoutNotice] = useState(null);
  const [checkoutNoticeFading, setCheckoutNoticeFading] = useState(false);

  // Auto-fade the checkout notice banner ~2s after it appears, regardless of
  // which call site set it.
  useEffect(() => {
    if (!checkoutNotice) {
      setCheckoutNoticeFading(false);
      return undefined;
    }

    setCheckoutNoticeFading(false);
    const fadeTimer = setTimeout(() => setCheckoutNoticeFading(true), 1700);
    const clearTimer = setTimeout(() => {
      setCheckoutNotice(null);
      setCheckoutNoticeFading(false);
    }, 2000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
    };
  }, [checkoutNotice]);

  // These flows opt out of the global error modal, so the inline notice has to
  // carry the whole message.
  const noticeFor = useApiErrorMessage();

  // A key identifies one checkout intent. It is reused across retries of the same
  // attempt so a double-submit replays rather than opening a second session, and
  // rotated whenever the session is discarded so the next attempt is genuinely new.
  const idempotencyKeyRef = useRef(null);
  const currentIdempotencyKey = useCallback(() => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = newIdempotencyKey();
    }
    return idempotencyKeyRef.current;
  }, []);

  const clearCheckoutSession = useCallback(() => {
    setCheckoutSessionId(null);
    setCheckoutTotals(null);
    idempotencyKeyRef.current = null;
  }, []);

  // Switching contact re-targets a different cart, so any session and key held for
  // the previous one must not be replayed against it.
  useEffect(() => {
    clearCheckoutSession();
  }, [contactId, clearCheckoutSession]);

  const [checkoutClicked, setCheckoutClicked] = useState(false);
  const [, setActiveTab] = useState("Cart");
  const [savedOpen, setSavedOpen] = useState(false);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [savedCardsModalOpen, setSavedCardsModalOpen] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  // Shared success notice for the shipping address and credit card save
  // flows; holds the message text, or null when hidden.
  const [successNotice, setSuccessNotice] = useState(null);

  // The backend re-validates stock at purchase time and invalidates its cart
  // cache the moment it rejects an order for that reason, so the safest
  // recovery is to abandon the checkout review and land back on the base
  // cart view, where a fresh CartGet will show the corrected item state.
  const handleOutOfStockCheckoutFailure = useCallback(
    (error) => {
      clearCheckoutSession();
      setCheckoutClicked(false);
      outOfStockModalShownRef.current = false;
      queryClient.invalidateQueries("cart");
      setCheckoutNotice(
        noticeFor(
          error,
          t(
            "errorModal.checkoutStockChanged",
            "Some items in your cart are no longer available in the requested quantity. Please review your cart before checking out again."
          )
        )
      );
      history.push("/cart");
    },
    [clearCheckoutSession, queryClient, history, noticeFor, t]
  );

  // Shared with the address-change re-checkout below, which must build the
  // payload from the just-saved address rather than the (stale, pre-render)
  // `shippingAddress` closure value.
  const buildCheckoutAddressPayload = (addr) =>
    addr
      ? {
          shipAddress1: addr.address1,
          shipAddress2: addr.address2 || null,
          shipCity: addr.city,
          shipState: addr.state,
          shipPostalCode: addr.zip,
          shipCountry: addr.country || "US",
        }
      : {};

  const checkoutAddressPayload = buildCheckoutAddressPayload(shippingAddress);

  const billedNowItems = useMemo(
    () => stockInfo.filter((i) => !i.isPreOrder),
    [stockInfo]
  );
  const billedLaterItems = useMemo(
    () => stockInfo.filter((i) => i.isPreOrder),
    [stockInfo]
  );

  const sumLines = (items) =>
    items.reduce(
      (sum, item) => sum + (item.memberPrice || 0) * (item.quantity || 0),
      0
    );

  const billedNowTotal = sumLines(billedNowItems);
  const billedLaterTotal = sumLines(billedLaterItems);

  // Totals come from the checkout session so the amount shown is the amount that
  // will be charged. Before checkout there is no session, so only the subtotal is known.
  const subTotalDue = readAmount(checkoutTotals?.subTotal, billedNowTotal);
  const shippingFee = readAmount(checkoutTotals?.shippingTotal, 0);
  const taxAmount = readAmount(checkoutTotals?.taxTotal, 0);
  const total = readAmount(checkoutTotals?.orderTotal, billedNowTotal);

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "My Orders", href: "/my-orders" },
    { label: "Cart", href: "/cart" },
  ];

  const handleTabClick = (item) => {
    setActiveTab(item.label);
    history.push(item.href);
  };

  const resolvePurchasedOrderId = (purchaseResponse) => {
    if (!purchaseResponse || typeof purchaseResponse !== "object") {
      return null;
    }

    return (
      purchaseResponse.orderId ??
      purchaseResponse.orderID ??
      purchaseResponse.id ??
      null
    );
  };

  const resolveCardLast4 = (card) => {
    const rawLast4 =
      card?.lastFour ?? card?.last4 ?? card?.cardLastFour ?? card?.lastDigits;
    const digits = String(rawLast4 ?? "").replace(/\D/g, "");
    return digits ? digits.slice(-4) : null;
  };

  const resolvePurchasePaymentMethodIdentifiers = (card) => {
    const membershipCreditCardId =
      card?.membershipCreditCardId ??
      card?.raw?.membershipCreditCardId ??
      card?.cardId ??
      null;

    const fallbackId = card?.id ?? null;

    const paymentMethodId = membershipCreditCardId ?? fallbackId;

    return {
      paymentMethodId,
      membershipCreditCardId,
    };
  };

  const modifiedArgs = {
    subTotal: billedNowTotal,
    tax: taxAmount,
    shippingFee,
    total,
    isSubmitting: checkout.isLoading || purchase.isLoading,
    disableCheckout:
      outOfStockCartItems.length > 0 || overStockCartItems.length > 0,
    onCheckout: () => {
      const { paymentMethodId, membershipCreditCardId } =
        resolvePurchasePaymentMethodIdentifiers(activeCard);

      // Only send membershipCreditCardId, not functionCreditCardId
      // This matches the reference implementation in wwdb-membership-sign-up-web

      setCheckoutNotice(null);
      checkout.mutate(
        {
          ...checkoutAddressPayload,
          paymentMethodId,
          membershipCreditCardId,
          creditCardId: membershipCreditCardId ?? null,
          ccId: paymentMethodId,
          idempotencyKey: currentIdempotencyKey(),
        },
        {
          onSuccess: (data) => {
            setCheckoutSessionId(data?.checkoutSessionId ?? data?.id ?? null);
            setCheckoutTotals(data ?? null);
            setCheckoutClicked(true);
            trackCheckoutStarted({
              checkoutSessionId: data?.checkoutSessionId ?? data?.id ?? null,
              items: billedNowItems,
              subTotal: data?.subTotal ?? billedNowTotal,
              tax: data?.taxTotal ?? 0,
              shipping: data?.shippingTotal ?? 0,
              total: data?.orderTotal ?? billedNowTotal,
            });
          },
          onError: (error) => {
            if (isOutOfStockError(error)) {
              handleOutOfStockCheckoutFailure(error);
              return;
            }
            // A stuck/reused idempotency key (or any 409 from checkout creation)
            // would otherwise retry forever against the same doomed key; rotate
            // so the next attempt starts a genuinely new checkout session.
            if (
              error?.status === 409 ||
              error?.code === "IDEMPOTENCY_KEY_REUSED"
            ) {
              clearCheckoutSession();
            }
            setCheckoutNotice(
              noticeFor(
                error,
                t(
                  "errorModal.checkoutStart",
                  "We could not start checkout. Please try again."
                )
              )
            );
          },
        }
      );
    },
    onPurchase: async () => {
      const { paymentMethodId, membershipCreditCardId } =
        resolvePurchasePaymentMethodIdentifiers(activeCard);

      // Only send membershipCreditCardId, not functionCreditCardId
      // This matches the reference implementation in wwdb-membership-sign-up-web
      // and prevents backend from using an incorrect linked card ID

      setCheckoutNotice(null);

      let currentCheckoutSessionId = checkoutSessionId;
      let resolvedCheckoutTotals = checkoutTotals;
      if (!currentCheckoutSessionId) {
        try {
          const checkoutResponse = await checkout.mutateAsync({
            ...checkoutAddressPayload,
            paymentMethodId,
            membershipCreditCardId,
            creditCardId: membershipCreditCardId ?? null,
            ccId: paymentMethodId,
            idempotencyKey: currentIdempotencyKey(),
          });
          currentCheckoutSessionId =
            checkoutResponse?.checkoutSessionId ?? checkoutResponse?.id ?? null;
          setCheckoutSessionId(currentCheckoutSessionId);
          setCheckoutTotals(checkoutResponse ?? null);
          resolvedCheckoutTotals = checkoutResponse ?? null;
          // Mirrors onCheckout's tracking - this branch only runs when
          // checkoutSessionId was cleared out from under an already-clicked
          // Checkout screen (e.g. a 409/idempotency-key rotation), so a new
          // session is created here without onCheckout's onSuccess ever
          // running for it. Without this, Order Completed/Payment Info
          // Entered would reference a checkout_id with no matching Checkout
          // Started event.
          trackCheckoutStarted({
            checkoutSessionId: currentCheckoutSessionId,
            items: billedNowItems,
            subTotal: checkoutResponse?.subTotal ?? billedNowTotal,
            tax: checkoutResponse?.taxTotal ?? 0,
            shipping: checkoutResponse?.shippingTotal ?? 0,
            total: checkoutResponse?.orderTotal ?? billedNowTotal,
          });
        } catch (error) {
          if (isOutOfStockError(error)) {
            handleOutOfStockCheckoutFailure(error);
            return;
          }
          // A stuck/reused idempotency key (or any 409 from checkout creation)
          // would otherwise retry forever against the same doomed key; rotate
          // so the next attempt starts a genuinely new checkout session.
          if (
            error?.status === 409 ||
            error?.code === "IDEMPOTENCY_KEY_REUSED"
          ) {
            clearCheckoutSession();
          }
          setCheckoutNotice(
            noticeFor(
              error,
              t(
                "errorModal.checkoutStart",
                "We could not start checkout. Please try again."
              )
            )
          );
          return;
        }
      }

      // Fires at submission, distinct from Order Completed below, so drop-off
      // between "submitted payment" and "order actually completed" (e.g. a
      // declined card) is visible in the funnel.
      trackPaymentInfoEntered({ checkoutSessionId: currentCheckoutSessionId });

      purchase.mutate(
        {
          checkoutSessionId: currentCheckoutSessionId,
          paymentMethodId,
          membershipCreditCardId,
          creditCardId: membershipCreditCardId ?? null,
          // Keep legacy field for backward compatibility while API clients migrate.
          ccId: paymentMethodId,
        },
        {
          onSuccess: (data) => {
            const purchasedOrderId = resolvePurchasedOrderId(data);
            const cardLast4 = resolveCardLast4(activeCard);
            const checkoutContext = {
              shippingAddress: shippingAddress
                ? {
                    address1: shippingAddress.address1 ?? null,
                    address2: shippingAddress.address2 ?? null,
                    city: shippingAddress.city ?? null,
                    state: shippingAddress.state ?? null,
                    zip: shippingAddress.zip ?? null,
                  }
                : null,
              paymentCard: {
                last4: cardLast4,
                cardType: activeCard?.cardType ?? activeCard?.type ?? "Card",
              },
            };

            trackOrderCompleted({
              orderId: purchasedOrderId,
              checkoutSessionId: currentCheckoutSessionId,
              items: billedNowItems,
              subTotal: resolvedCheckoutTotals?.subTotal ?? billedNowTotal,
              tax: resolvedCheckoutTotals?.taxTotal ?? 0,
              shipping: resolvedCheckoutTotals?.shippingTotal ?? 0,
              total: resolvedCheckoutTotals?.orderTotal ?? billedNowTotal,
            });

            if (purchasedOrderId) {
              history.push({
                pathname: `/order/${encodeURIComponent(purchasedOrderId)}`,
                search: "?isNew=true",
                state: { checkoutContext },
              });
              return;
            }

            history.push("/my-orders");
          },
          onError: (error) => {
            if (isOutOfStockError(error)) {
              handleOutOfStockCheckoutFailure(error);
              return;
            }

            // Any 409 from the purchase path means the session is spent or stale.
            // Some of those come back as an unstructured string, so status is the
            // reliable signal; the code only refines the wording. A 406 is a card
            // decline, which leaves the session usable for a retry.
            const sessionIsDead =
              error?.status === 409 ||
              RECOVERABLE_CHECKOUT_CODES.has(error?.code);

            if (sessionIsDead) {
              clearCheckoutSession();
              queryClient.invalidateQueries("cart");
              setCheckoutNotice(
                noticeFor(
                  error,
                  t(
                    "errorModal.cartChanged",
                    "Your cart has changed. Please review your order."
                  )
                )
              );
              return;
            }

            setCheckoutNotice(
              noticeFor(
                error,
                t(
                  "errorModal.purchase",
                  "We could not complete your purchase. Please try again."
                )
              )
            );
          },
        }
      );
    },
    onCancel: () => {
      // fully restore pre-checkout state
      setCheckoutClicked(false);
      setSavedOpen(false);
      setCheckoutNotice(null);
      clearCheckoutSession();
    },
    checkoutClicked,
  };

  const buildAddCardPayload = (customerData = {}) => {
    const rawExpiration = String(
      customerData?.cardExpiration ?? customerData?.expiration ?? ""
    );
    const expirationDigits = rawExpiration
      .replace(/\D/g, "")
      .padStart(4, "0")
      .slice(-4);

    return {
      cardHolder:
        customerData?.cardHolder ??
        `${customerData?.first_name ?? ""} ${
          customerData?.last_name ?? ""
        }`.trim(),
      expiration: expirationDigits,
      firstSix: String(
        customerData?.cardFirstSix ?? customerData?.cc_bin ?? ""
      ),
      lastFour: String(
        customerData?.cardLastFour ?? customerData?.lastFour ?? ""
      ),
      nickname: customerData?.nickname ?? "",
      token:
        customerData?.vaultId ??
        customerData?.customer_vault_id ??
        customerData?.token,
      postalCode:
        customerData?.postalCode ?? customerData?.cardPostalCode ?? "",
      isPrimary: Boolean(customerData?.isPrimary),
      ibo: customerData?.ibo,
    };
  };

  return (
    <div className={styles.container}>
      {/* Desktop-only top nav */}
      <div className={styles.desktopNav}>
        <Navbar
          onClick={() => {}}
          isHome={true}
          menuItems={menuItems}
          activeTab={t("Cart")}
          onTabClick={handleTabClick}
          cartCount={cartCount}
        />
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.cartHeader}>
          <CartHeader numOfItems={cartCount} />
        </div>

        {strandedItemCount > 0 && (
          <div className={styles.checkoutNotice} role="status">
            {strandedItemCount === 1
              ? t("1 item is no longer available and has been set aside.")
              : t(
                  "{{count}} items are no longer available and have been set aside.",
                  { count: strandedItemCount }
                )}
          </div>
        )}

        <Modal
          isOpen={isOutOfStockModalOpen}
          onClose={() => setIsOutOfStockModalOpen(false)}
        >
          <div
            className={styles.outOfStockModalContent}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="outOfStockModalTitle"
          >
            <span className={styles.outOfStockModalTitle}>
              {t("Items No Longer Available")}
            </span>
            <span className={styles.outOfStockModalBody}>
              {t(
                "The item(s) in cart, {{items}}, are no longer available. Please remove them from your cart before proceeding.",
                {
                  items: outOfStockCartItems
                    .map((item) => item?.title)
                    .filter(Boolean)
                    .join(", "),
                }
              )}
            </span>
          </div>
          <div className={styles.outOfStockModalButtonBox}>
            <Button
              onClick={() => setIsOutOfStockModalOpen(false)}
              text={t("Okay")}
              className="closeBtn"
            />
          </div>
        </Modal>

        <div className={styles.divider} />

        {!checkoutClicked && (
          <>
            <div className={styles.cartListContainer}>
              <CartItemList
                stockInfo={stockInfo}
                isLoading={isCartLoading}
                onDeleteItem={(item) =>
                  removeFromCart.mutate(item.id, {
                    onSuccess: () =>
                      trackProductRemoved(item, {
                        cartId: cartData?.cartId ?? cartData?.id,
                      }),
                  })
                }
                onSave={(updatedItem) => {
                  const itemId = updatedItem?.id;
                  if (!itemId) return;

                  // Optimistic update: update the cache immediately
                  queryClient.setQueryData(["cart"], (oldData) => {
                    if (!oldData?.items) return oldData;
                    return {
                      ...oldData,
                      items: oldData.items.map((item) =>
                        item.id === itemId
                          ? {
                              ...item,
                              quantity: updatedItem?.quantity ?? 1,
                              ...(updatedItem?.variantId !== undefined
                                ? { variantId: updatedItem.variantId }
                                : {}),
                              ...(updatedItem?.color !== undefined
                                ? { color: updatedItem.color }
                                : {}),
                              ...(updatedItem?.size !== undefined
                                ? { size: updatedItem.size }
                                : {}),
                            }
                          : item
                      ),
                    };
                  });

                  // Then update via API
                  updateCartItem.mutate({
                    itemId,
                    quantity: updatedItem?.quantity ?? 1,
                    ...(updatedItem?.variantId !== undefined
                      ? { variantId: updatedItem.variantId }
                      : {}),
                  });
                }}
              />
            </div>

            {/* Subtotal only */}
            <motion.div
              className={styles.subtotalSection}
              key={isCartLoading ? "loading" : "loaded"}
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              <div className={styles.subtotalRow}>
                <span className={styles.subtotalLabel}>Subtotal</span>
                <span className={styles.subtotalValue}>
                  ${billedNowTotal.toFixed(2)}
                </span>
              </div>
              <div className={styles.savedDivider} />

              <div
                className={styles.savedHeaderRow}
                onClick={() => setSavedOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    setSavedOpen((v) => !v);
                }}
              >
                <span className={styles.savedHeaderText}>Saved For Later</span>
                <ChevronRight
                  className={`${styles.savedChevron} ${
                    savedOpen ? styles.savedChevronOpen : ""
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className={styles.savedDivider} />
            </motion.div>

            {savedOpen && (
              <div className={styles.savedListWrapper}>
                <CartItemList
                  stockInfo={savedItems}
                  isLoading={isSavedForLaterLoading}
                  variant="saved"
                  showTitle={false}
                  onSave={(updatedItem, savedItem) => {
                    // The edit modal on saved rows is the "Add To Cart"
                    // modal: it only exists to pick a variant/quantity, then
                    // perform the same add-to-cart the cart icon does.
                    if (!savedItem) return;

                    handleAddSavedItemToCart({
                      ...savedItem,
                      variationId:
                        updatedItem?.variationId ??
                        savedItem?.variationId ??
                        null,
                      variantId:
                        updatedItem?.variantId ?? savedItem?.variantId ?? null,
                      quantity: updatedItem?.quantity ?? savedItem?.quantity,
                      color: updatedItem?.color ?? savedItem?.color,
                      size: updatedItem?.size ?? savedItem?.size,
                    });
                  }}
                  onAddToCart={handleAddSavedItemToCart}
                  onDeleteItem={(item) =>
                    removeListItem.mutate({
                      listId: savedForLaterList?.listId,
                      listProductId: item?.id ?? item?.listProductId,
                      stockId: item?.stockId ?? item?.productId,
                    })
                  }
                />
              </div>
            )}
          </>
        )}

        {checkoutNotice && (
          <div
            className={`${styles.checkoutNotice} ${
              checkoutNoticeFading ? styles.checkoutNoticeFading : ""
            }`}
            role="alert"
          >
            {checkoutNotice}
          </div>
        )}

        {checkoutClicked && (
          <>
            {/* Billed Later / Total Due Later (preorders) */}
            {billedLaterItems.length > 0 && (
              <div className={styles.billedLaterHeader}>Total Due Later</div>
            )}
            {billedLaterItems.map((it) => (
              <div className={styles.miniRow} key={`later-${it.id}`}>
                <ProductImage
                  src={it.image}
                  alt={it.title}
                  className={styles.miniImg}
                />
                <div className={styles.miniTitle}>
                  {it.title}{" "}
                  <span className={styles.preorderTag}>(pre-order)</span>
                </div>
                <div className={styles.miniPrice}>
                  ${it.memberPrice ? it.memberPrice.toFixed(2) : "0.00"}
                </div>
              </div>
            ))}

            {billedNowItems.length > 0 && (
              <div className={styles.billedNowHeader}>Total Due Now</div>
            )}
            {billedNowItems.map((it) => (
              <div className={styles.miniRow} key={`now-${it.id}`}>
                <ProductImage
                  src={it.image}
                  alt={it.title}
                  className={styles.miniImg}
                />
                <div className={styles.miniTitle}>{it.title}</div>
                <div className={styles.miniPrice}>
                  ${it.memberPrice ? it.memberPrice.toFixed(2) : "0.00"}
                </div>
              </div>
            ))}

            {billedLaterItems.length > 0 && (
              <>
                <div className={styles.billedSeparateRow}>
                  <span className={styles.billedSeparateLabel}>
                    Items billed separately
                  </span>
                  <span className={styles.billedSeparateValue}>
                    ${billedLaterTotal.toFixed(2)}
                  </span>
                </div>
                <div className={orderStyles.receiptDivider}></div>
              </>
            )}

            <div className={orderStyles.receiptItems}>
              <div className={orderStyles.subtotalRow}>
                <span className={orderStyles.subtotalLabel}>Subtotal</span>
                <span className={orderStyles.receiptValue}>
                  ${subTotalDue.toFixed(2)}
                </span>
              </div>
              <div className={orderStyles.taxRow}>
                <span className={orderStyles.taxLabel}>Shipping Amount</span>
                <span className={orderStyles.receiptValue}>
                  ${shippingFee.toFixed(2)}
                </span>
              </div>
              <div className={orderStyles.taxRow}>
                <span className={orderStyles.taxLabel}>Tax</span>
                <span className={orderStyles.receiptValue}>
                  ${taxAmount.toFixed(2)}
                </span>
              </div>
              <div className={orderStyles.totalRow}>
                <span className={orderStyles.totalLabel}>Order Total</span>
                <span className={orderStyles.summaryValueBold}>
                  ${total.toFixed(2)}
                </span>
              </div>
              <div className={orderStyles.receiptDivider}></div>
            </div>

            {/* Billing Address */}
            <div className={orderStyles.billingSection}>
              <div className={orderStyles.billingInfo}>
                <span className={orderStyles.billingLabel}>
                  Shipping Address
                </span>
                <div className={orderStyles.billingAddress}>
                  {(shippingAddress?.recipientName ||
                    shippingAddress?.recipient) && (
                    <span className={orderStyles.billingRecipient}>
                      {shippingAddress.recipientName ??
                        shippingAddress.recipient}
                    </span>
                  )}
                  <span>{shippingAddress?.address1 ?? "Î“Ã‡Ã¶"}</span>
                  <span>
                    {shippingAddress?.city}, {shippingAddress?.state}{" "}
                    {shippingAddress?.zip}
                  </span>
                </div>
              </div>

              <button
                className={`${orderStyles.editButton} ${orderStyles.billingEditButton}`}
                onClick={() => setShippingModalOpen(true)}
                type="button"
                disabled={checkout.isLoading || purchase.isLoading}
              >
                Edit
              </button>
            </div>

            <div className={orderStyles.receiptDivider}></div>

            {/* Payment */}
            <div className={orderStyles.paymentSection}>
              <span className={orderStyles.paymentLabel}>Pay With</span>
              <div className={orderStyles.paymentInfo}>
                <div className={orderStyles.cardInfo}>
                  <FontAwesomeIcon
                    icon={getCardBrandIcon(activeCard?.cardType)}
                    title={activeCard?.cardType ?? "Card"}
                    className={orderStyles.cardIcon}
                  />
                  <span className={orderStyles.cardNumber}>
                    *{activeCard?.lastFour ?? "----"}
                  </span>
                </div>
                <button
                  className={orderStyles.editButton}
                  onClick={() => setSavedCardsModalOpen(true)}
                  type="button"
                  disabled={checkout.isLoading || purchase.isLoading}
                >
                  Edit
                </button>
              </div>
              <div className={orderStyles.receiptDivider}></div>
            </div>
          </>
        )}
      </div>

      <div
        className={
          checkoutClicked
            ? styles.footerSpacerLarge
            : savedOpen
            ? styles.footerSpacerMedium
            : styles.footerSpacerSmall
        }
      />

      <div className={styles.footerContainer}>
        <CartTotalDetails {...modifiedArgs} />
      </div>
      <ShippingAddressModal
        isOpen={shippingModalOpen}
        onClose={() => setShippingModalOpen(false)}
        onSave={(address) => {
          setSelectedAddress(address);
          clearCheckoutSession();
          setShippingModalOpen(false);

          // Already on the review screen: re-quote against the new address
          // immediately instead of leaving stale/zeroed totals until the user
          // clicks Checkout or Place Your Order again. clearCheckoutSession()
          // already rotated the idempotency key, so this call mints a fresh
          // session (supersedes the old one) rather than replaying it. The
          // Edit button is disabled while checkout/purchase is in flight (see
          // isSubmitting below), so this can't overlap with another checkout
          // mutate call.
          if (checkoutClicked) {
            const { paymentMethodId, membershipCreditCardId } =
              resolvePurchasePaymentMethodIdentifiers(activeCard);

            setCheckoutNotice(null);
            checkout.mutate(
              {
                ...buildCheckoutAddressPayload(address),
                paymentMethodId,
                membershipCreditCardId,
                creditCardId: membershipCreditCardId ?? null,
                ccId: paymentMethodId,
                idempotencyKey: currentIdempotencyKey(),
              },
              {
                onSuccess: (data) => {
                  setCheckoutSessionId(
                    data?.checkoutSessionId ?? data?.id ?? null
                  );
                  setCheckoutTotals(data ?? null);
                  // Only confirm success once the new totals actually landed,
                  // so a failed re-quote doesn't show a contradictory
                  // "saved successfully" notice alongside the error notice.
                  setSuccessNotice(t("Address saved successfully."));
                },
                onError: (error) => {
                  if (isOutOfStockError(error)) {
                    handleOutOfStockCheckoutFailure(error);
                    return;
                  }
                  // A stuck/reused idempotency key (or any 409 from checkout
                  // creation) would otherwise retry forever against the same
                  // doomed key; rotate so the next attempt starts fresh.
                  if (
                    error?.status === 409 ||
                    error?.code === "IDEMPOTENCY_KEY_REUSED"
                  ) {
                    clearCheckoutSession();
                  }
                  setCheckoutNotice(
                    noticeFor(
                      error,
                      t(
                        "errorModal.checkoutStart",
                        "We could not start checkout. Please try again."
                      )
                    )
                  );
                },
              }
            );
          } else {
            setSuccessNotice(t("Address saved successfully."));
          }
        }}
      />
      <NotificationOverlay
        overlayText={successNotice}
        isVisible={Boolean(successNotice)}
        onDismiss={() => setSuccessNotice(null)}
        variant="success"
      />
      <SavedCardsModal
        isOpen={savedCardsModalOpen}
        onClose={() => setSavedCardsModalOpen(false)}
        onSave={(savePayload) => {
          const cardList = Array.isArray(savePayload)
            ? savePayload
            : savePayload?.cards ?? [];
          const explicitlySelectedCard = Array.isArray(savePayload)
            ? null
            : savePayload?.selectedCard ?? null;

          if (explicitlySelectedCard) {
            const selectedId =
              explicitlySelectedCard?.membershipCreditCardId ??
              explicitlySelectedCard?.cardId ??
              explicitlySelectedCard?.id ??
              null;
            // onSave runs after an await, so React 17 does not batch these;
            // batching keeps selectedCardId and preview from being out of sync.
            unstable_batchedUpdates(() => {
              setSelectedCardId(selectedId);
              setSelectedCardPreview(explicitlySelectedCard);
              clearCheckoutSession();
              setSavedCardsModalOpen(false);
              setSuccessNotice(t("Card saved successfully."));
            });
            return;
          }

          const selectedFromModal = cardList.find(
            (c) => c.isDefault || c.isPrimary
          );
          const picked = selectedFromModal || cardList[0] || null;
          const pickedId =
            picked?.membershipCreditCardId ??
            picked?.cardId ??
            picked?.id ??
            null;

          unstable_batchedUpdates(() => {
            setSelectedCardId(pickedId);
            setSelectedCardPreview(picked);
            clearCheckoutSession();
            setSavedCardsModalOpen(false);
            setSuccessNotice(t("Card saved successfully."));
          });
        }}
        onAdd={() => {
          // Close the saved-cards modal before opening Add Card so only one
          // full-page modal is mounted at a time. Collect.js's injected
          // iframes freeze up (buttons stop responding) when its host modal
          // is stacked on top of another still-open modal.
          setSavedCardsModalOpen(false);
          setAddCardOpen(true);
        }}
      />
      <Modal isOpen={addCardOpen} className={styles.addCardModal}>
        <CardPaymentModel
          onCancel={() => {
            setAddCardOpen(false);
            setSavedCardsModalOpen(true);
          }}
          onUpdatePayment={async (customerData) => {
            const payload = buildAddCardPayload(customerData);
            await addPaymentMethod.mutateAsync(payload);
            setAddCardOpen(false);
            setSavedCardsModalOpen(true);
          }}
        />
      </Modal>
      <BottomNav cartCount={cartCount} />
    </div>
  );
}

export default CartPage;
